import { DateTime, TimeUnit, TimeZone } from 'timezonecomplete';

import type { IDst, IWeek, IWeekDay } from '@/types/calendar';
import type { BlockId, IBlock } from '@/types/block';

import { WeekDays } from '@/store/enums';
import { daysToMillis } from '@/helpers';
import { getDlsOffsetDiff, getNextDST, getTimezoneAbbreviation } from '@/helpers/timezone';
import { DAYS_IN_WEEK } from '@/config/variables';
import { CachedBlock } from '@/classes/cachedBlock';

export class Week implements IWeek {
  private readonly _weekId: number;
  private readonly _timezone: string;
  private readonly _days: IWeekDay[];
  private readonly _startBlock: IBlock;
  private readonly _endBlock: IBlock;
  private readonly _dst: IDst | null;
  private readonly _weekStartTotalOffset: number;
  private readonly _tzAbbreviation: string;

  constructor(weekId: number, timezoneName: string) {
    this._weekId = weekId;
    this._timezone = timezoneName;
    this._startBlock = CachedBlock.fromWeekId(weekId, timezoneName);
    this._endBlock = CachedBlock.fromWeekId(weekId, timezoneName, WeekDays.Sunday, 'end');
    this._dst = this._getDstData();
    this._weekStartTotalOffset = this._startBlock.timeZoneOffset;
    this._tzAbbreviation = this._startBlock.getTimezoneAbbreviation();
    this._days = this._getWeekDays();
  }

  private _getWeekDays(): IWeekDay[] {
    const startWeekDate = new DateTime(this._startBlock.utcMillis, TimeZone.utc()).convert(
      TimeZone.zone(this._timezone),
    );
    return [...Array(7).keys()].map<IWeekDay>((i: number) => {
      const currentWeekDayZoned = startWeekDate.add(i, TimeUnit.Day);

      return {
        dayYear: Number(currentWeekDayZoned.format('yyyy')),
        dayNumber: Number(currentWeekDayZoned.format('d')),
        dayName: currentWeekDayZoned.format('EEE'),
        monthNumber: Number(currentWeekDayZoned.format('M')),
        isCurrentDay: currentWeekDayZoned.dayOfYear() === DateTime.now(TimeZone.zone(this._timezone)).dayOfYear(),
        isDlsDay:
          !!this._dst &&
          currentWeekDayZoned.dayOfYear() ===
            new DateTime(this._dst.block.utcMillis, TimeZone.zone(this._timezone)).dayOfYear(),
        startBlock: CachedBlock.getBlockIdByMillis(currentWeekDayZoned.unixUtcMillis()),
        endBlock: CachedBlock.getBlockIdByMillis(
          currentWeekDayZoned.add(1, TimeUnit.Day).sub(1, TimeUnit.Millisecond).unixUtcMillis(),
        ),
        tzAbbreviation: this.hasDstTransition
          ? getTimezoneAbbreviation(this._timezone, currentWeekDayZoned.unixUtcMillis())
          : this._tzAbbreviation,
      };
    });
  }

  private _getDstData(): IDst | null {
    const nextDst = getNextDST(this._timezone, this._startBlock.utcMillis);
    if (!nextDst) {
      return null;
    }
    const isDstInGivenWeek = Math.abs(nextDst - this._startBlock.utcMillis) < daysToMillis(DAYS_IN_WEEK + 1);
    if (!isDstInGivenWeek) return null;

    const date = new Date(nextDst);
    const tzOffsetDiff = getDlsOffsetDiff(date, this._timezone);

    const dstBlock = CachedBlock.fromMillis(nextDst, this._timezone);

    const firstBlockId = dstBlock.blockId;
    const secondBlockId = firstBlockId + tzOffsetDiff;

    const [startBlockId, endBlockId] =
      firstBlockId < secondBlockId ? [firstBlockId, secondBlockId - 1] : [secondBlockId, firstBlockId - 1];

    const endBlock = CachedBlock.fromBlockId(endBlockId, this._timezone);

    return {
      date,
      block: dstBlock,
      blockId: CachedBlock.getBlockIdByMillis(nextDst),
      tzOffsetDiff,
      startBlock: startBlockId,
      endBlock: endBlockId,
      tzAbbreviation: endBlock.getTimezoneAbbreviation(),
    };
  }

  getDay(blockId: BlockId): IWeekDay | null {
    // check if the block belongs to the week
    if (blockId < this._startBlock.blockId || this._endBlock.blockId < blockId) {
      return null;
    }
    const day = this._days.find(day => day.startBlock <= blockId && blockId <= day.endBlock);
    return day ?? null;
  }

  get weekId(): number {
    return this._weekId;
  }

  get timezone(): string {
    return this._timezone;
  }

  get days(): IWeekDay[] {
    return this._days;
  }

  get startBlock(): IBlock {
    return this._startBlock;
  }

  get endBlock(): IBlock {
    return this._endBlock;
  }

  get dst(): IDst | null {
    return this._dst;
  }

  get weekStartTotalOffset(): number {
    return this._weekStartTotalOffset;
  }

  get tzAbbreviation(): string {
    return this._tzAbbreviation;
  }

  get hasDstTransition(): boolean {
    return !!this._dst;
  }
}
